import { api } from './generated-client'
import type {
  User,
  LoginCredentials,
  LoginResponse,
  RefreshResponse,
} from '@/types/auth'

// 後端 /auth/me 回應格式
interface MeResponse {
  userId: number
  tenantId: number
  email: string
  role: string
  roles?: string[]
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const { data, error } = await api.POST('/auth/login', { body: credentials })
    if (error || !data) {
      // Surface recognizable backend error codes with a helpful message so the
      // "access without role" case is not confused with a wrong password.
      // See ADR-014 (NO_CRM_ROLE_IN_TENANT).
      const errBody = error as unknown as { code?: string; error?: string } | undefined
      const code = errBody?.code
      const serverMsg = errBody?.error
      if (code === 'NO_CRM_ROLE_IN_TENANT') {
        throw new Error(
          serverMsg ||
            '您在此品牌沒有 CRM 角色。請改用您有角色的品牌登入，或使用 Console 登入。',
        )
      }
      throw new Error(serverMsg || '登入失敗')
    }
    return data as LoginResponse
  },

  // STAB-P1-01 S2: refreshToken is optional (client may already have lost it
  // by the time logout runs), but when present it MUST be sent so the server
  // can revoke the refresh lineage too — not just the access JWT's session id.
  // Body shape must match backend `LogoutRequest` (camelCase `refreshToken`).
  async logout(refreshToken?: string): Promise<void> {
    const { error } = await api.POST('/auth/logout', {
      body: refreshToken ? { refreshToken } : {},
    })
    if (error) throw new Error('Logout failed')
  },

  // STAB-P1-01 S2: 公開端點 (不需要 Bearer access token)。
  // 後端會：
  //   - 用 `refreshToken` 查 auth_sessions
  //   - ROTATE：發新一對 (accessToken + refreshToken)，把舊 refresh token 標記已用
  //   - 偵測 REUSE：若 refresh token 已被用過 → 全家族撤銷 → 401
  //   - tenant 隔離：refresh token 綁定當初 mint 的 tenant；X-Tenant 錯誤 → 403
  //   - rate limit：429
  // 失敗時拋出 Error（讓 caller 走 forced-relogin 流程）。
  // MUST route through @/api/generated-client (CLAUDE.md §6)。
  async refresh(refreshToken: string): Promise<RefreshResponse> {
    if (!refreshToken) {
      throw new Error('refresh: empty refreshToken')
    }
    const { data, error } = await api.POST('/auth/refresh', {
      body: { refreshToken },
    })
    if (error || !data) {
      const errBody = error as unknown as { error?: string; message?: string } | undefined
      const serverMsg = errBody?.error || errBody?.message
      throw new Error(serverMsg || 'refresh failed')
    }
    // 後端 `RefreshTokenResponse`（camelCase 序列化）→ 對齊 `RefreshResponse`。
    // 注意：舊版 generated types 只暴露 `token`；同步後會是 `accessToken` + `refreshToken`。
    const payload = data as unknown as Partial<RefreshResponse> & { token?: string }
    if (!payload.accessToken || !payload.refreshToken) {
      // 形狀不對：可能 spec-hash 沒同步；視為 forced-relogin（不要 silently cast）。
      throw new Error('refresh: malformed response (missing accessToken/refreshToken)')
    }
    return {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      sessionId: payload.sessionId,
      expiresAt: payload.expiresAt,
    }
  },

  async me(): Promise<User> {
    const { data, error } = await api.GET('/auth/me')
    if (error || !data) throw new Error('Failed to fetch user info')
    const meData = data as MeResponse
    return {
      id: meData.userId,
      email: meData.email,
      name: meData.email.split('@')[0],
      role: meData.role,
      roles: meData.roles ?? [meData.role],
      tenantId: meData.tenantId,
    }
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const { error } = await api.PUT('/auth/password', {
      body: { oldPassword, newPassword }
    })
    if (error) throw new Error('Failed to change password')
  },

  /**
   * 回傳 challengeId：確認端要帶回去的範圍鍵。手機簡訊碼**必帶**（每挑戰 5 次錯碼
   * 即作廢）；Email 連結 token 可不帶。帳號不存在時後端也回一個值（不洩漏存在性）。
   */
  async requestPasswordResetByEmail(email: string): Promise<{ challengeId: string; expiresInMinutes: number }> {
    const { data, error } = await api.POST('/auth/password/reset/by-email', {
      body: { email }
    })
    if (error || !data) throw new Error('Failed to request password reset')
    return { challengeId: data.challengeId, expiresInMinutes: data.expiresInMinutes }
  },

  async requestPasswordResetByMobile(mobile: string): Promise<{ challengeId: string; expiresInMinutes: number }> {
    const { data, error } = await api.POST('/auth/password/reset/by-mobile', {
      body: { mobile }
    })
    if (error || !data) throw new Error('Failed to request password reset')
    return { challengeId: data.challengeId, expiresInMinutes: data.expiresInMinutes }
  },

  /**
   * @param challengeId 發送端回傳的挑戰識別碼；簡訊碼必帶，Email token 可省略。
   */
  async confirmPasswordReset(token: string, newPassword: string, challengeId?: string): Promise<void> {
    const { error } = await api.POST('/auth/password/reset/confirm', {
      body: { token, newPassword, challengeId: challengeId ?? null }
    })
    if (error) throw new Error('Failed to confirm password reset')
  },

  async sendVerifyEmailCode(): Promise<void> {
    const { error } = await api.POST('/auth/verify/email/send')
    if (error) throw new Error('Failed to send verification email')
  },

  async confirmVerifyEmail(code: string): Promise<void> {
    const { error } = await api.POST('/auth/verify/email/confirm', {
      body: { code }
    })
    if (error) throw new Error('Failed to verify email')
  },

  async sendVerifyMobileCode(): Promise<void> {
    const { error } = await api.POST('/auth/verify/mobile/send')
    if (error) throw new Error('Failed to send verification SMS')
  },

  async confirmVerifyMobile(code: string): Promise<void> {
    const { error } = await api.POST('/auth/verify/mobile/confirm', {
      body: { code }
    })
    if (error) throw new Error('Failed to verify mobile')
  },
}
